import React, { useState } from "react";
import { createPortal } from "react-dom";

// Mobile bottom sheets for the Agent Radar surface (design source, 402px frame).
// Each replaces a desktop popover; all touch targets ≥44px. Shared Sheet chrome
// (portal, backdrop, grab handle, rounded top) keeps them consistent.

const Sheet = ({ onClose, children, padding = "8px 16px 40px" }) =>
  createPortal(
    <div className="fixed inset-0 z-[70] flex items-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-[rgba(17,24,39,0.4)]" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full bg-white rounded-t-[18px]" style={{ padding }}>
        <div className="w-9 h-1 rounded-full bg-[#DDE3EB] mx-auto mt-1 mb-3" aria-hidden="true" />
        {children}
      </div>
    </div>,
    document.body,
  );

const Label = ({ children }) => (
  <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#9CA3AF]" style={{ padding: "0 2px 6px" }}>
    {children}
  </div>
);

const rowBtn = "flex items-center w-full text-left bg-transparent border-none rounded-[9px] cursor-pointer font-medium";

// ── Lists dropdown sheet ───────────────────────────────────────────────────────
export const MobileListSheet = ({ lists, selectedList, onSelect, onNewList, onOpenOpts, onClose }) => (
  <Sheet onClose={onClose}>
    <Label>Lists</Label>
    {lists.map((l) => {
      const selected = selectedList === l.id;
      return (
        <div key={l.id} className="flex items-center cursor-pointer" style={{ gap: 11, minHeight: 52, padding: "0 2px" }} onClick={() => onSelect(l.id)}>
          <span style={{ width: 18, color: selected ? "#2283FF" : "transparent", fontWeight: 800 }}>✓</span>
          <span className="flex-1 min-w-0 text-[14.5px] font-semibold text-[#111827] overflow-hidden text-ellipsis whitespace-nowrap">{l.name}</span>
          <span className="text-[12px] font-semibold text-[#9CA3AF] flex-shrink-0">{l.entry_count ?? 0}</span>
          {!l.is_default && (
            <button
              type="button"
              aria-label={`${l.name} options`}
              onClick={(e) => { e.stopPropagation(); onOpenOpts(l); }}
              className="border-none bg-transparent text-[#9CA3AF] cursor-pointer leading-none flex-shrink-0"
              style={{ width: 44, height: 44, margin: "0 -10px 0 0", fontSize: 16, fontWeight: 700, padding: 0 }}
            >
              ⋯
            </button>
          )}
        </div>
      );
    })}
    <div style={{ height: 1, background: "#EEF1F5", margin: "4px 0" }} />
    <button type="button" onClick={onNewList} className={`${rowBtn} text-[14.5px] text-[#2283FF]`} style={{ minHeight: 52, padding: "0 2px" }}>
      + New list
    </button>
  </Sheet>
);

// ── Entry actions sheet (⋯ on a card) ──────────────────────────────────────────
export const MobileEntrySheet = ({ entry, lists, selectedList, onMove, onCopy, onShare, onRemove, onClose }) => {
  const [mode, setMode] = useState(null); // null | 'move' | 'copy'
  const listIds = entry.list_ids || [];
  const pick = mode === "move" ? lists.filter((l) => l.id !== selectedList) : lists.filter((l) => !listIds.includes(l.id));

  return (
    <Sheet onClose={onClose} padding="8px 12px 40px">
      {!mode ? (
        <>
          <div className="text-[12px] font-semibold text-[#9CA3AF]" style={{ padding: "0 10px 8px" }}>{entry.name}</div>
          <button type="button" onClick={() => setMode("move")} className={`${rowBtn} text-[15px] text-[#111827]`} style={{ minHeight: 52, padding: "0 10px" }}>Move to list…</button>
          <button type="button" onClick={() => setMode("copy")} className={`${rowBtn} text-[15px] text-[#111827]`} style={{ minHeight: 52, padding: "0 10px" }}>Copy to list…</button>
          <button type="button" onClick={() => onShare(entry)} className={`${rowBtn} text-[15px] text-[#111827]`} style={{ minHeight: 52, padding: "0 10px" }}>Share to chat</button>
          <div style={{ height: 1, background: "#EEF1F5", margin: "4px 10px" }} />
          <button type="button" onClick={() => onRemove(entry)} className={`${rowBtn} text-[15px] text-[#DC2626]`} style={{ minHeight: 52, padding: "0 10px" }}>Remove from radar</button>
        </>
      ) : (
        <>
          <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#9CA3AF]" style={{ padding: "2px 10px 6px" }}>{mode === "move" ? "Move to…" : "Copy to…"}</div>
          {pick.map((l) => (
            <button key={l.id} type="button" onClick={() => (mode === "move" ? onMove : onCopy)(entry, l)} className={`${rowBtn} text-[15px] text-[#111827]`} style={{ minHeight: 52, padding: "0 10px" }}>{l.name}</button>
          ))}
          {pick.length === 0 && <div className="text-[13px] text-[#9CA3AF]" style={{ padding: "14px 10px" }}>No other lists yet</div>}
          <div style={{ height: 1, background: "#EEF1F5", margin: "4px 10px" }} />
          <button type="button" onClick={() => setMode(null)} className={`${rowBtn} text-[15px] text-[#9CA3AF]`} style={{ minHeight: 52, padding: "0 10px" }}>‹ Back</button>
        </>
      )}
    </Sheet>
  );
};

// ── Filter & sort sheet ────────────────────────────────────────────────────────
export const MobileFilterSheet = ({ posOptions, labelOptions, sortOptions, filterPos, filterLabel, sortBy, setFilterPos, setFilterLabel, setSortBy, onClose }) => {
  const chip = (sel) => ({
    flexShrink: 0, height: 44, padding: "0 15px", borderRadius: 22,
    border: sel ? "1px solid #BFD8FF" : "1px solid #DDE3EB",
    background: sel ? "#EAF2FF" : "#FFFFFF", color: sel ? "#1B6FE0" : "#374151",
    fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
    display: "inline-flex", alignItems: "center", gap: 7,
  });
  const smallChip = (sel) => ({ ...chip(sel), height: 40, padding: "0 14px", borderRadius: 20, border: sel ? "1px solid #2283FF" : "1px solid #DDE3EB" });

  return (
    <Sheet onClose={onClose}>
      <Label>Position</Label>
      <div className="flex flex-wrap" style={{ gap: 7, marginBottom: 16 }}>
        {posOptions.map((o) => (
          <button key={o.value} type="button" onClick={() => setFilterPos(o.value)} style={chip(filterPos === o.value)}>{o.label}</button>
        ))}
      </div>
      <Label>Label</Label>
      <div className="flex flex-wrap" style={{ gap: 7, marginBottom: 16 }}>
        {labelOptions.map((o) => (
          <button key={o.value} type="button" onClick={() => setFilterLabel(o.value)} style={smallChip(filterLabel === o.value)}>{o.label}</button>
        ))}
      </div>
      <Label>Sort by</Label>
      {sortOptions.map((o) => {
        const sel = sortBy === o.value;
        return (
          <button key={o.value} type="button" onClick={() => setSortBy(o.value)} className="flex items-center w-full text-left bg-transparent border-none cursor-pointer font-medium text-[14.5px] text-[#111827]" style={{ gap: 10, minHeight: 50, padding: "0 2px" }}>
            <span className="flex items-center justify-center" style={{ width: 18, height: 18, borderRadius: "50%", border: sel ? "5px solid #2283FF" : "1.5px solid #C7D0DC" }} />
            {o.label}
          </button>
        );
      })}
      <button type="button" onClick={onClose} className="w-full bg-[#2283FF] text-white border-none rounded-[9px] text-[14px] font-semibold cursor-pointer" style={{ height: 48, marginTop: 10 }}>Done</button>
    </Sheet>
  );
};

// ── New-list sheet ─────────────────────────────────────────────────────────────
export const MobileNewListSheet = ({ onCreate, onClose }) => {
  const [name, setName] = useState("");
  const create = () => { const v = name.trim(); if (v) onCreate(v); };
  return (
    <Sheet onClose={onClose}>
      <div className="text-[15px] font-bold text-[#111827]" style={{ marginBottom: 12 }}>New list</div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && create()}
        autoFocus
        placeholder="List name"
        maxLength={60}
        className="w-full outline-none"
        style={{ height: 46, border: "1px solid #DDE3EB", borderRadius: 9, padding: "0 12px", fontSize: 14, marginBottom: 12 }}
      />
      <div className="flex" style={{ gap: 8 }}>
        <button type="button" onClick={create} className="flex-1 bg-[#2283FF] text-white border-none rounded-[9px] text-[14px] font-semibold cursor-pointer" style={{ height: 48 }}>Create</button>
        <button type="button" onClick={onClose} className="flex-1 bg-white border border-[#DDE3EB] rounded-[9px] text-[14px] font-semibold text-[#374151] cursor-pointer" style={{ height: 48 }}>Cancel</button>
      </div>
    </Sheet>
  );
};

// ── List options sheet (rename / delete for a custom list) ──────────────────────
export const MobileListOptsSheet = ({ list, onRename, onDeleteRequest, onClose }) => {
  const [renaming, setRenaming] = useState(false);
  const [val, setVal] = useState(list.name);
  const save = () => { const v = val.trim(); if (v) onRename(list.id, v); onClose(); };

  return (
    <Sheet onClose={onClose}>
      <div className="text-[12px] font-semibold text-[#9CA3AF]" style={{ padding: "0 2px 8px" }}>{list.name}</div>
      {renaming ? (
        <>
          <input
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            autoFocus
            maxLength={60}
            className="w-full outline-none"
            style={{ height: 46, border: "1px solid #2283FF", borderRadius: 9, padding: "0 12px", fontSize: 14, marginBottom: 12 }}
          />
          <button type="button" onClick={save} className="w-full bg-[#2283FF] text-white border-none rounded-[9px] text-[14px] font-semibold cursor-pointer" style={{ height: 48 }}>Save name</button>
        </>
      ) : (
        <>
          <button type="button" onClick={() => setRenaming(true)} className={`${rowBtn} text-[15px] text-[#111827]`} style={{ minHeight: 52, padding: "0 2px" }}>Rename</button>
          <div style={{ height: 1, background: "#EEF1F5", margin: "4px 0" }} />
          <button type="button" onClick={() => onDeleteRequest(list)} className={`${rowBtn} text-[15px] text-[#DC2626]`} style={{ minHeight: 52, padding: "0 2px" }}>Delete list</button>
        </>
      )}
    </Sheet>
  );
};
